import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import type { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import PublicHeader from "../components/PublicHeader";
import { getPublicAthletePage, PublicAthlete } from "../firebase/publicDirectory";
import { useTranslation } from "../hooks/useTranslation";
import "./PublicDirectory.css";

const initials = (athlete: PublicAthlete) => `${athlete.firstName?.[0] || ""}${athlete.lastName?.[0] || ""}`.toUpperCase();

export default function PublicAthletes() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  const [athletes, setAthletes] = useState<PublicAthlete[]>([]);
  const [cursor, setCursor] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getPublicAthletePage({ search: submittedSearch, pageSize: 24 })
      .then((page) => { if (active) { setAthletes(page.items); setCursor(page.cursor); setHasMore(page.hasMore); } })
      .catch((err) => { console.error("Public athlete directory load failed", err); if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [submittedSearch]);

  const loadMore = async () => {
    if (!cursor || loading) return;
    setLoading(true);
    try {
      const page = await getPublicAthletePage({ search: submittedSearch, cursor, pageSize: 24 });
      setAthletes((current) => [...current, ...page.items]);
      setCursor(page.cursor);
      setHasMore(page.hasMore);
    } catch (err) {
      console.error("More public athletes could not be loaded", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return <div className="directory-page">
    <Helmet><title>{t("publicDirectory.athletes.seoTitle")}</title><meta name="description" content={t("publicDirectory.athletes.subtitle")} /></Helmet>
    <PublicHeader />
    <header className="directory-hero"><div className="directory-hero__inner">
      <p className="directory-eyebrow">{t("publicDirectory.officialRegistry")}</p>
      <h1>{t("publicDirectory.athletes.title")}</h1>
      <p>{t("publicDirectory.athletes.subtitle")}</p>
    </div></header>
    <main className="directory-content">
      <div className="directory-notice">{t("publicDirectory.athletes.progressive")}</div>
      <form className="directory-search" onSubmit={(event) => { event.preventDefault(); setSubmittedSearch(search); }}>
        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("publicDirectory.athletes.search")} aria-label={t("publicDirectory.athletes.search")} />
        <span aria-hidden="true">⌕</span>
        <button type="submit">Search</button>
      </form>
      {error && <div className="directory-error" role="alert">{t("publicDirectory.loadError")}</div>}
      <div className="directory-grid">
        {!loading && !error && athletes.length === 0 && <div className="directory-empty"><h2>{t("publicDirectory.athletes.emptyTitle")}</h2><div>{t("publicDirectory.athletes.emptyText")}</div></div>}
        {athletes.map((athlete) => <Link className="directory-card" to={`/athletes/${athlete.slug}`} key={athlete.id}>
          <div className="directory-card__top"><div className="directory-avatar directory-avatar--round">{athlete.imageUrl ? <img src={athlete.imageUrl} alt="" /> : initials(athlete)}</div><div><h2>{athlete.firstName} {athlete.lastName}</h2><span className="directory-card__id">{athlete.athleteId}</span></div></div>
          <div className="directory-card__meta">
            <span>{t("publicDirectory.club")}<b>{athlete.currentClub || t("publicDirectory.notPublished")}</b></span>
            {athlete.nationality && <span>{t("publicDirectory.nationality")}<b>{athlete.nationality}</b></span>}
          </div>
          <div className="directory-card__action">{t("publicDirectory.viewProfile")} →</div>
        </Link>)}
      </div>
      {loading && <div className="directory-empty">{t("publicDirectory.loading")}</div>}
      {hasMore && !loading && <div className="directory-load-more"><button onClick={loadMore}>{t("publicDirectory.loadMore")}</button></div>}
    </main>
  </div>;
}
